<script setup lang="ts">
// src/SideWinder.Web/src/components/DealingPlanCard.vue
//
// Target-weight rebalancing plan Task 5 (spec 2026-08-05-target-weight-
// rebalancing.md §3.4/§5): read-only display of the 08:40 UK PlanBuilder's
// persisted dealing plan (DealingPlanTickService -> DealingPlanBuilder ->
// DealingPlanRepository, GET /api/user/dealing-plan). Mounted directly
// below StrategyAllocationCard on ExecPanel for every portfolio subdomain —
// this is a household-wide plan across BGA/JH/GOLD, not fund-scoped.
//
// A 404 from the endpoint means "no plan exists for today" (weekend, bank
// holiday, or before the 08:40 tick has run yet) — rendered as an empty
// state, not an error. Any other failure (network, 500) renders the
// generic error banner.
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type DealingPlanView, type DealingPlanLeg } from '../api/client'

const plan = ref<DealingPlanView | null>(null)
const loading = ref(true)
const notFound = ref(false)
const error = ref<string | null>(null)

// Re-evaluated on every render via `now` so the 08:57-10:00 banner window
// and the poll-window check both track the wall clock without needing a
// dedicated ref per condition.
const now = ref(new Date())

let pollTimer: ReturnType<typeof setInterval> | null = null

// Current UK wall-clock time as minutes-since-midnight. Uses Intl with an
// explicit Europe/London timeZone (same approach as utils/ukStrike.ts)
// rather than manual UTC-offset math, so BST/GMT is handled automatically.
function nowUkMinutes(at: Date): number {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/London',
      hourCycle: 'h23',
      hour: 'numeric',
      minute: 'numeric',
    })
    const parts = fmt.formatToParts(at)
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10)
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
    return hour * 60 + minute
  } catch {
    return at.getUTCHours() * 60 + at.getUTCMinutes()
  }
}

const POLL_WINDOW_START_MIN = 8 * 60 + 39  // 08:39
const POLL_WINDOW_END_MIN = 9 * 60 + 5     // 09:05
const BANNER_WINDOW_START_MIN = 8 * 60 + 57 // 08:57
const BANNER_WINDOW_END_MIN = 10 * 60       // 10:00

function isInPollWindow(at: Date): boolean {
  const mins = nowUkMinutes(at)
  return mins >= POLL_WINDOW_START_MIN && mins <= POLL_WINDOW_END_MIN
}

function isInBannerWindow(at: Date): boolean {
  const mins = nowUkMinutes(at)
  return mins >= BANNER_WINDOW_START_MIN && mins < BANNER_WINDOW_END_MIN
}

// HL-app phone wording (Paolo 2026-10-30: a leg the phone was still selling read
// as a real failure). The API keeps the stored statuses (failed / expired_manual
// drive the 09:00 sweep and the contract-note booker) and says it in the reason;
// these strings mirror SellInAppAlert.WithPhoneMarker / PhoneSellCoordinator.LegOutcome.
const WITH_PHONE_MARKER = '| HL-app phone: selling now'
const SOLD_BY_PHONE_PREFIX = 'Sold in the HL app by the phone'
const PREDICTED_LOCK_PREFIX = 'RemainingValueZero: predicted at publish (HL T+1 settlement lock)'

function isWithPhone(leg: DealingPlanLeg): boolean {
  return (leg.status === 'pending' || leg.status === 'in_flight' || leg.status === 'failed')
    && (leg.statusReason ?? '').includes(WITH_PHONE_MARKER)
}

function isSoldByPhone(leg: DealingPlanLeg): boolean {
  return leg.status === 'expired_manual' && (leg.statusReason ?? '').startsWith(SOLD_BY_PHONE_PREFIX)
}

const showFailureBanner = computed(() => {
  const legs = (plan.value?.legs ?? []).filter(l => !isWithPhone(l))
  if (legs.some(l => l.status === 'failed')) return true
  if (isInBannerWindow(now.value)) {
    return legs.some(l => l.status === 'pending' || l.status === 'in_flight')
  }
  return false
})

// Account-first view (Paolo 2026-10-20): he deals the leftovers by hand in
// the HL app, which lists accounts ISA → SIPP → SIPP Income Drawdown, so the
// plan is grouped per account in that order (sells before buys inside each)
// instead of per fund. Unknown account names sort last, by account id.
const ACCOUNT_RANK: [RegExp, number][] = [
  [/\bISA\b/i, 0],
  [/fund\s*&\s*share/i, 1],
  [/income\s+drawdown/i, 3],
  [/\bSIPP\b/i, 2],
]

function accountRank(name: string): number {
  for (const [re, rank] of ACCOUNT_RANK) if (re.test(name)) return rank
  return 99
}

// Fund titles as HL's pending-orders / holdings pages print them, so a row
// reads the same as the line Paolo taps in the app.
const FUND_HL_NAME: Record<string, string> = {
  BGA: 'Baillie Gifford American',
  JH: 'Janus Henderson Global Technology Leaders',
  GOLD: 'Ninety One Global Gold',
  GGE: 'WS Guinness Global Energy',
  JAPAN: 'L&G Japan Index Trust',
  CHARTERIS: 'YFS Charteris Gold & Precious Metals',
  AMATI: 'WS Amati Strategic Metals',
  MINERS: 'ES Baker Steel Gold & Precious Metals',
  JAPANESG: 'L&G Future World ESG Japan Index',
  TECH: 'Liontrust Global Technology',
  ELECTRUM: 'SVS Baker Steel Electrum',
  JHTECH: 'Janus Henderson Sustainable Future Technologies',
  ASIATECH: 'L&G Pacific Index Trust',
  BARINGSOIL: 'Barings Global Resources',
  SPWORLD: 'Fidelity Open World',
  SMALLCAP: 'CT US Smaller Companies Integrated',
}

function fundName(fundId: string): string {
  return FUND_HL_NAME[fundId.toUpperCase()] ?? fundId
}

const DONE_STATUSES = new Set(['executed', 'skipped'])

interface AccountGroup {
  accountId: number
  accountName: string
  legs: DealingPlanLeg[]
  remaining: number
  withPhone: number
}

const accountGroups = computed<AccountGroup[]>(() => {
  const byId = new Map<number, AccountGroup>()
  for (const leg of plan.value?.legs ?? []) {
    let g = byId.get(leg.accountId)
    if (!g) {
      g = { accountId: leg.accountId, accountName: leg.accountName || `acct ${leg.accountId}`, legs: [], remaining: 0, withPhone: 0 }
      byId.set(leg.accountId, g)
    }
    g.legs.push(leg)
    if (isWithPhone(leg)) g.withPhone++
    else if (!DONE_STATUSES.has(leg.status) && !isSoldByPhone(leg)) g.remaining++
  }
  const groups = [...byId.values()]
  for (const g of groups) {
    // Stable sort: sells first, then buys, original plan order otherwise.
    g.legs = g.legs
      .map((leg, i) => ({ leg, i }))
      .sort((a, b) => (sideRank(a.leg.side) - sideRank(b.leg.side)) || (a.i - b.i))
      .map(x => x.leg)
  }
  return groups.sort((a, b) =>
    (accountRank(a.accountName) - accountRank(b.accountName)) || (a.accountId - b.accountId))
})

function sideRank(side: string): number {
  return side.startsWith('SELL') ? 0 : 1
}

function sideBadgeClass(side: string): string {
  switch (side) {
    case 'BUY': return 'dp-badge-buy'
    case 'SELL_FULL': return 'dp-badge-sell-full'
    case 'SELL_PARTIAL': return 'dp-badge-sell-partial'
    default: return 'dp-badge-other'
  }
}

function statusChipClass(leg: DealingPlanLeg): string {
  if (isWithPhone(leg)) return 'dp-status-with_phone'
  if (isSoldByPhone(leg)) return 'dp-status-sold_by_phone'
  return `dp-status-${leg.status}`
}

// Manual trader (Auto-Execute OFF): nothing is traded for them, so a pending leg is theirs
// to deal and an expired one was not dealt. A missing flag (older API) means unchanged labels.
const manualTrader = computed(() => plan.value?.autoExecute === false)

function statusLabel(leg: DealingPlanLeg): string {
  if (isWithPhone(leg)) return 'with phone'
  if (isSoldByPhone(leg)) return 'sold by phone'
  if (manualTrader.value && leg.status === 'pending') return 'To deal yourself'
  if (manualTrader.value && leg.status === 'expired_manual') return 'Not dealt'
  return leg.status
}

function remainingLabel(g: AccountGroup): string {
  if (g.remaining > 0) return `${g.remaining} to do`
  return g.withPhone > 0 ? '📱 phone selling' : '✓ done'
}

// The internal refusal token stays in the stored reason (backend helpers key on
// it); the screen shows it in words.
function displayReason(reason: string): string {
  return reason
    .replace(PREDICTED_LOCK_PREFIX, 'HL T+1 settlement lock')
    .replace(` ${WITH_PHONE_MARKER}`, ' — website skipped; the phone is selling it now')
    .replace(' | HL-app phone: ', ' — phone: ')
}

function displayAmount(leg: DealingPlanLeg): string {
  if (leg.side.startsWith('SELL') && leg.estUnits !== null) {
    return `${leg.estUnits.toLocaleString(undefined, { maximumFractionDigits: 2 })} units`
  }
  return `£${leg.estAmountGbp.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

function basisLine(leg: DealingPlanLeg): string {
  return `target ${leg.basis.targetPct.toFixed(1)}% → actual ${leg.basis.actualPct.toFixed(1)}%`
}

async function fetchPlan() {
  error.value = null
  try {
    plan.value = await api.getDealingPlan()
    notFound.value = false
  } catch (e: any) {
    const msg: string = e?.message ?? ''
    if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
      notFound.value = true
      plan.value = null
    } else {
      error.value = msg || 'Failed to load dealing plan'
    }
  } finally {
    loading.value = false
  }
}

function tick() {
  now.value = new Date()
  // Self-heal (2026-08-06 incident): a transient fetch failure —
  // classically a sleeping tab's wake-up poll racing network reconnect —
  // must not jam the card in the error state for the rest of the day.
  // Outside the poll window we still retry on the 30s tick whenever the
  // last fetch errored; a successful fetch stops the polling again.
  if (isInPollWindow(now.value) || error.value !== null) {
    void fetchPlan()
  }
}

onMounted(async () => {
  await fetchPlan()
  pollTimer = setInterval(tick, 30000)
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
})

defineExpose({ plan, showFailureBanner, accountGroups, fetchPlan })
</script>

<template>
  <section class="dp-card" data-testid="dealing-plan-card">
    <header class="dp-header">
      <h3>Dealing Plan</h3>
    </header>

    <div v-if="showFailureBanner" class="dp-banner" data-testid="dealing-plan-banner">
      ⚠ Unfilled legs — manual completion needed before 09:00.
    </div>

    <div v-if="loading" class="dp-loading" data-testid="dealing-plan-loading">Loading…</div>

    <div v-else-if="notFound" class="dp-empty" data-testid="dealing-plan-empty">
      No dealing plan yet — builds at 08:40 on dealing days.
    </div>

    <div v-else-if="error" class="dp-error" data-testid="dealing-plan-error">{{ error }}</div>

    <template v-else-if="plan">
      <div
        v-for="group in accountGroups"
        :key="group.accountId"
        class="dp-account-group"
        :data-testid="`dealing-plan-account-${group.accountId}`"
      >
        <h4 class="dp-account-name">
          <span data-testid="dealing-plan-account-name">{{ group.accountName }}</span>
          <span
            class="dp-remaining"
            :class="group.remaining > 0 ? 'dp-remaining-todo' : group.withPhone > 0 ? 'dp-remaining-phone' : 'dp-remaining-done'"
            data-testid="dealing-plan-account-remaining"
          >{{ remainingLabel(group) }}</span>
        </h4>

        <div
          v-for="(leg, idx) in group.legs"
          :key="`${group.accountId}-${idx}`"
          class="dp-leg-row"
          :data-testid="`dealing-plan-leg-${group.accountId}-${idx}`"
        >
          <span class="dp-fund" data-testid="dealing-plan-fund">{{ fundName(leg.fundId) }}</span>
          <span class="dp-badge" :class="sideBadgeClass(leg.side)" data-testid="dealing-plan-side">{{ leg.side }}</span>
          <span class="dp-amount" data-testid="dealing-plan-amount">{{ displayAmount(leg) }}</span>
          <span class="dp-basis" data-testid="dealing-plan-basis">{{ basisLine(leg) }}</span>
          <span
            class="dp-status"
            :class="statusChipClass(leg)"
            data-testid="dealing-plan-status"
          >{{ statusLabel(leg) }}</span>
          <span v-if="leg.statusReason" class="dp-reason" data-testid="dealing-plan-reason">{{ displayReason(leg.statusReason) }}</span>
        </div>
      </div>
    </template>
  </section>
</template>

<style scoped>
.dp-card { border: 1px solid #333; border-radius: 4px; margin-bottom: 12px; padding: 0.75rem 1rem 1rem; }
.dp-header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 0.5rem; }
.dp-header h3 { margin: 0; font-size: 0.9rem; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }

.dp-banner {
  padding: 8px 10px; margin-bottom: 0.6rem; background: #3a1b1b; border: 1px solid #f44336;
  border-radius: 4px; color: #f44336; font-size: 0.8rem; font-weight: 600;
}

.dp-loading { color: #666; font-style: italic; font-size: 0.85rem; }
.dp-empty { color: #8b949e; font-size: 0.85rem; font-style: italic; }
.dp-error { padding: 8px 10px; background: #3a1b1b; border: 1px solid #f44336; border-radius: 4px; color: #f44336; font-size: 0.8rem; }

.dp-account-group { margin-bottom: 0.8rem; }
.dp-account-name {
  display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
  margin: 0 0 0.35rem; font-size: 0.85rem; color: #ccc; font-weight: 600;
}
.dp-remaining { font-size: 0.72rem; font-weight: 600; padding: 2px 8px; border-radius: 10px; white-space: nowrap; }
.dp-remaining-todo { background: #3a2f1b; color: #ff9800; }
.dp-remaining-done { background: #1b3a1b; color: #4caf50; }
.dp-remaining-phone { background: #3a2e12; color: #d29922; }
.dp-fund { flex-basis: 100%; font-weight: 600; color: #e6edf3; }

.dp-leg-row {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem;
  padding: 0.4rem 0; border-bottom: 1px solid #2a2a2a; font-size: 0.82rem; color: #c9d1d9;
}

.dp-badge {
  font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.03em; min-width: 84px; text-align: center;
}
.dp-badge-buy { background: #1b3a1b; color: #4caf50; }
.dp-badge-sell-full { background: #3a1b1b; color: #f44336; }
.dp-badge-sell-partial { background: #3a2f1b; color: #ff9800; }
.dp-badge-other { background: #21262d; color: #8b949e; }

.dp-amount { font-weight: 600; }
.dp-basis { color: #8b949e; font-size: 0.78rem; }

.dp-status {
  margin-left: auto; font-size: 0.72rem; padding: 2px 8px; border-radius: 10px;
  background: #21262d; color: #8b949e; text-transform: uppercase; letter-spacing: 0.03em;
}
.dp-status-pending { background: #21262d; color: #8b949e; }
.dp-status-in_flight { background: #12202e; color: #58a6ff; }
.dp-status-executed { background: #1b3a1b; color: #4caf50; }
.dp-status-failed { background: #3a1b1b; color: #f44336; }
.dp-status-skipped { background: #1a1a1a; color: #555; }
.dp-status-expired_manual { background: transparent; color: #f44336; border: 1px solid #f44336; }
.dp-status-with_phone { background: #3a2e12; color: #d29922; }
.dp-status-sold_by_phone { background: #1b3a1b; color: #4caf50; }

.dp-reason { color: #d29922; font-size: 0.76rem; font-style: italic; }

@media (max-width: 600px) {
  .dp-leg-row { flex-direction: column; align-items: stretch; }
  .dp-status { margin-left: 0; }
}
</style>
